import { Box, Grid, Modal } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchSelect from 'react-select';
import { Close } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomButton from '../../shared/CustomButton';
import useFetchData from '../hooks/useFetchData';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { showProgress } from '../../../redux/actions/DealershipActions';
import service from '../../../apis/service';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import CustomDropDown from '../../shared/CustomDropDown';

const style = {
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '527px',
  backgroundColor: '#F2F4F7',
  outline: 'none',
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: 'none',
  },
};

const useStyle = makeStyles({
  headerConatiner: {
    width: '527px',
    height: '72px',
    padding: '24px 16px 24px 16px',
  },
  contentContainer: {
    width: '495px',
    padding: '16px',
  },
  buttonContainer: {
    marginTop: 'auto',
    background: '#FFFFFF',
    width: '527px',
    height: '64px',
    border: '1px',
  },
});

const EditZombie = ({ isOpen, handleClose, zombie }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { dealershipList, deptList, rolesList, getRoles } = useFetchData();
  const [employeeDealership, setEmployeeDealership] = useState(null);
  const [roleID, setRoleID] = useState(null);
  const [deptID, setDeptID] = useState(null);

  const dealershipOptions = useMemo(
    () =>
      dealershipList?.map((dealer) => ({
        label: dealer?.name,
        value: dealer?.id,
      })) || [],
    [dealershipList],
  );

  useEffect(() => {
    if (dealershipList && zombie?.dealershipId) {
      const matched = dealershipList.find((d) => d?.id === zombie?.dealershipId);
      if (matched) {
        setEmployeeDealership({ label: matched.name, value: matched.id });
      }
    }
  }, [dealershipList, zombie]);

  const formatOptionLabel = useCallback(
    ({ label, value }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={classes.ellipsisMessage}>{label}</div>
        {employeeDealership?.value !== value ? <div>{value}</div> : null}
      </div>
    ),
    [classes.ellipsisMessage, employeeDealership],
  );

  const handleChangeDept = (event) => {
    setDeptID(event.target.value);
    getRoles(event.target.value);
  };

  const handleChangeRole = (event) => {
    setRoleID(event.target.value);
  };

  const handleChangeDealership = (selectedOption) => {
    setEmployeeDealership(selectedOption);
  };

  const handleSave = async () => {
    const payload = {
      ...(!zombie?.dealershipId && { dealershipId: employeeDealership?.value }),
      ...(!zombie?.departmentRoleId && { departmentRoleId: roleID }),
    };

    dispatch(showProgress(true));
    try {
      await service.put(apiEndpoints.UPDATE_ZOMBIE.replace('ID', zombie?.hash), payload);
      notifySuccess('Employee updated successfully');
      handleClose();
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  return (
    <div>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.headerConatiner}
          >
            <Grid item>
              <CustomPopupText text="Edit Zombie" fontSize="20px" fontWeight="600" color="#000" />
            </Grid>
            <Grid item>
              <Close onClick={handleClose} style={{ cursor: 'pointer' }} />
            </Grid>
          </Grid>
          <Grid container direction="column" className={classes.contentContainer} spacing={1}>
            {!zombie?.dealershipId && (
              <Grid item>
                <Grid container direction="column" spacing={1}>
                  <Grid item>
                    <CustomPopupText text="Select Dealership" />
                  </Grid>
                  <Grid item>
                    <SearchSelect
                      placeholder="Select Employee Dealership"
                      value={employeeDealership}
                      onChange={handleChangeDealership}
                      options={dealershipOptions}
                      formatOptionLabel={formatOptionLabel}
                      maxMenuHeight={200}
                      className={classes.dealershipChangeField}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#f4f4f4',
                        }),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!zombie?.departmentRoleId && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item>
                    <CustomPopupText text="Department" required />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomDropDown
                      width="480px"
                      value={deptList?.find((dept) => dept?.id === deptID)?.title || 'Select a value'}
                      name="department"
                      list={deptList}
                      handleChange={handleChangeDept}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!zombie?.departmentRoleId && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item>
                    <CustomPopupText text="Role" required />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomDropDown
                      width="480px"
                      value={rolesList?.find((role) => role?.id === roleID)?.title || 'Select a value'}
                      name="role"
                      list={rolesList}
                      handleChange={handleChangeRole}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid container justifyContent="flex-end" style={{ height: '100%' }}>
            <Grid item className={classes.buttonContainer}>
              <Grid
                container
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                style={{ height: '100%' }}
                spacing={1}
              >
                <Grid item>
                  <CustomButton label="Cancel" type="secondary" onClick={handleClose} />
                </Grid>
                <Grid item style={{ marginRight: '6px' }}>
                  <CustomButton label="Save" type="primary" onClick={handleSave} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

export default EditZombie;
